<template>
  <l-modal
    :title="t('product-discount.title')"
    :button-text="t('product-discount.save')"
    :button-enabled="discountIsValid"
    @action="save"
    @close="emit('close')"
  >
    <l-option-selector v-model="editingDiscount.type" :options="discountTypes" />

    <l-field :label="t('product-discount.discount-to-apply')" class="mt-4">
      <l-number-input
        v-if="editingDiscount.type === 'currency'"
        v-model="editingDiscount.amount"
        type="number"
        :min="0"
        format="currency"
        :icon="discountIcon"
      />
      <l-number-input
        v-else
        v-model="editingDiscount.amount"
        type="number"
        :min="0"
        format="decimal"
        :icon="discountIcon"
      />
    </l-field>

    <l-field :label="t('bill-discount.concept')">
      <l-textarea v-model="editingDiscount.concept" />
    </l-field>
  </l-modal>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { ProductPriceCalculator } from '@last/core'
import type { LIconName } from '@last/core-ui/paprika'
import { LField, LModal, LNumberInput, LOptionSelector, LTextarea } from '@last/core-ui/paprika'

import { useConfirm } from '@/plugins/managerConfirmation'
import { useConfigStore } from '@/store/config'
import { useTabsStore } from '@/store/tabs'
import type { TabProduct } from '@/types'

const { t } = useI18n()
const configStore = useConfigStore()
const tabsStore = useTabsStore()

const confirm = useConfirm()

const props = defineProps<{
  product: TabProduct
}>()

const emit = defineEmits<{
  close: []
}>()

const { currencyIcon } = storeToRefs(configStore)

const editingDiscount = ref<{
  type: string
  amount: number | null
  concept: string | null
}>({
  type: 'percentage',
  amount: null,
  concept: null
})

onMounted(() => {
  if (props.product.discountType) {
    editingDiscount.value.type = props.product.discountType
    editingDiscount.value.amount =
      props.product.discountType === 'cash'
        ? props.product.discountAmount! / 100
        : props.product.discountAmount!
    editingDiscount.value.concept = props.product.discountConcept ?? null
  }
})

const discountIcon = computed<LIconName>(() => {
  if (editingDiscount.value.type === 'percentage') {
    return 'percentage'
  }
  return currencyIcon.value
})

const discountTypes = computed(() => [
  {
    icon: currencyIcon.value as LIconName,
    value: 'cash',
    label: t('product-discount.cash')
  },
  {
    icon: 'percentage' as LIconName,
    value: 'percentage',
    label: t('product-discount.percentage')
  }
])

const discountIsValid = computed(() => {
  return (
    editingDiscount.value !== null &&
    editingDiscount.value.amount !== null &&
    !isNaN(editingDiscount.value.amount) &&
    ((editingDiscount.value.type === 'cash' &&
      editingDiscount.value.amount * 100 <= props.product.fullPrice) ||
      (editingDiscount.value.type === 'percentage' && editingDiscount.value.amount <= 100)) &&
    editingDiscount.value.type !== null
  )
})

async function save() {
  if (!discountIsValid.value) return
  const hasPrivilege = await confirm('DISCOUNT_MANAGER')
  if (!hasPrivilege) return

  const sendingAmount =
    editingDiscount.value.type === 'cash'
      ? editingDiscount.value.amount! * 100
      : editingDiscount.value.amount
  updateDiscount({ ...editingDiscount.value, amount: sendingAmount })
  emit('close')
}

async function updateDiscount(discount: any) {
  const productPricing = ProductPriceCalculator.calculateProductPricing({
    ...props.product,
    discountType: discount.type,
    discountAmount: discount.amount
  })
  if (+discount.amount === 0) {
    removeProductDiscount()
    return
  }
  if (props.product.quantity > 1) {
    const splittedProductId = tabsStore.splitProduct({
      productId: props.product.id,
      seat: props.product.seat
    })
    tabsStore.updateProductDiscount({
      productId: splittedProductId as string,
      discount: {
        discountType: discount.type,
        discountAmount: discount.amount,
        discountConcept: discount.concept,
        promotionId: null
      },
      productPricing: {
        ...productPricing
      }
    })
  } else {
    tabsStore.updateProductDiscount({
      productId: props.product.id,
      discount: {
        discountType: discount.type,
        discountAmount: discount.amount,
        discountConcept: discount.concept,
        promotionId: null
      },
      productPricing: {
        ...productPricing
      }
    })
  }
}

function removeProductDiscount() {
  const productPricing = ProductPriceCalculator.calculateProductPricing({
    ...props.product,
    discountType: null,
    discountAmount: null
  })
  tabsStore.updateProductDiscount({
    productId: props.product.id,
    discount: {
      discountType: null,
      discountAmount: null,
      discountConcept: null,
      promotionId: null
    },
    productPricing: {
      ...productPricing
    }
  })
}
</script>
